// src/exportRemovedDrugs.js
//
// Builds the "Removed drugs — <year>" workbook for the head-office
// date-checking view. One workbook with:
//   • one tab per pharmacy: the full year's removed drugs, same columns as the
//     on-screen popup, plus a (hidden) month-number column, a Unit price (£)
//     column and a Value (£) column. Value is a LIVE FORMULA (= unit price ×
//     qty), so editing qty or unit price on the sheet recalculates it.
//   • a Summary tab: group name, export date, and a grid of pharmacy rows ×
//     Jan–Dec columns. Each cell shows only the VALUE for that month, and is a
//     LIVE FORMULA — SUMIF over that pharmacy's sheet — so changing a value on a
//     pharmacy sheet flows through to the summary on recalculation.
//
// Prices arrive from the backend in PENCE (dmddata.price); we write pounds onto
// the sheets so the on-sheet maths is in £. Rows with price === null had no
// priced dmddata match — their unit price/value cells are left blank and shaded
// amber, and any summary month containing such a drug is shaded amber too (the
// value shown there is understated).

import ExcelJS from "exceljs";

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
];

const AMBER_ARGB = "FFFEF3C7";   // matches the UI amber (#fef3c7)
const HEADER_ARGB = "FFEFEFEF";

function sanitizeSheetName(name) {
  return (name || "Sheet").replace(/[\\/*?:[\]]/g, "").substring(0, 31) || "Sheet";
}

function fill(cell, argb) {
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb } };
}

function boldRow(row) {
  row.eachCell(cell => { cell.font = { bold: true }; });
}

// Quote a sheet name for use inside a formula. Excel needs single quotes around
// names with spaces/punctuation, and any embedded single quote doubled.
function sheetRef(name) {
  return `'${String(name).replace(/'/g, "''")}'`;
}

/**
 * payload shape (from /date-checking/removed-export):
 * {
 *   groupName, year, generatedAt,
 *   pharmacies: [
 *     { pharmacyId, pharmacyName,
 *       drugs: [ { drugName, location, expiryDate, quantity, batchNumber,
 *                  serialNumber, removedAt, removedAtIso, month (0-11),
 *                  price (pence|null) } ] }
 *   ]
 * }
 *
 * displayName: optional (name, masked) => string, so masking carries into the
 * workbook. masked: boolean.
 */
export async function exportRemovedDrugs(payload, {
  displayName = (n) => n,
  masked = false
} = {}) {
  const {
    groupName = "",
    year,
    generatedAt,
    pharmacies = []
  } = payload || {};

  const shownName = (raw) => displayName(raw || "Unknown", masked);
  const exportDate = generatedAt ? new Date(generatedAt) : new Date();
  const dateStamp = exportDate.toISOString().slice(0, 10);
  const timestamp = exportDate.toLocaleString("en-GB");

  const workbook = new ExcelJS.Workbook();
  workbook.created = exportDate;

  // Add the Summary first so it's the opening tab, but only fill it after the
  // pharmacy sheets exist (we need their final names + row spans to reference).
  const summary = workbook.addWorksheet("Summary", {
    views: [{ state: "frozen", xSplit: 1, ySplit: 5 }]
  });

  const sorted = [...pharmacies].sort((a, b) =>
    shownName(a.pharmacyName).localeCompare(shownName(b.pharmacyName)));

  // ----------------------------------------------------------------------
  // ONE TAB PER PHARMACY (built first so the Summary can reference them)
  // ----------------------------------------------------------------------
  // Per-pharmacy column layout (1-indexed):
  //   A Drug  B Location  C Expiry  D Qty  E Batch  F Removed on
  //   G Month#(hidden)  H Unit price (£)  I Value (£) = H*D
  const COL = { qty: "D", month: "G", unit: "H", value: "I" };

  const usedNames = new Set(["Summary"]);
  const uniqueSheetName = (base) => {
    let name = sanitizeSheetName(base);
    if (!usedNames.has(name)) { usedNames.add(name); return name; }
    let i = 1, candidate;
    do {
      const suffix = `_${i++}`;
      candidate = name.substring(0, 31 - suffix.length) + suffix;
    } while (usedNames.has(candidate));
    usedNames.add(candidate);
    return candidate;
  };

  // Track, per pharmacy, its final sheet name and which months had any
  // missing-price drug (for amber flagging on the summary).
  const sheetInfo = [];   // { ph, sheetName, monthMissing[12], hasData, span }

  for (const ph of sorted) {
    const sheetName = uniqueSheetName(shownName(ph.pharmacyName));
    const sheet = workbook.addWorksheet(sheetName);

    const drugs = [...(ph.drugs || [])].sort((a, b) => {
      const ad = a.removedAtIso || "", bd = b.removedAtIso || "";
      return ad.localeCompare(bd) ||
        (a.drugName || "").localeCompare(b.drugName || "");
    });

    sheet.addRow([groupName || "Pharmace"]);
    sheet.addRow([`Pharmacy: ${shownName(ph.pharmacyName)}`]);
    sheet.addRow([`Removed drugs — ${year}`]);
    sheet.addRow([`Exported: ${timestamp}`]);
    sheet.addRow([]);
    const totalPacketsRow = sheet.addRow(["Total packets removed:", ""]);
    const totalValueRow = sheet.addRow(["Total value (£):", ""]);
    sheet.addRow([]);

    const cols = ["Drug", "Location", "Expiry", "Qty", "Batch",
                  "Removed on", "Month#", "Unit price (£)", "Value (£)"];
    const hr = sheet.addRow(cols);
    boldRow(hr);
    hr.eachCell(cell => fill(cell, HEADER_ARGB));
    const headerRowNum = hr.number;
    const firstDataRow = headerRowNum + 1;

    const monthMissing = new Array(12).fill(false);
    const monthValue = new Array(12).fill(0);   // £, cached for formula results
    let sheetPackets = 0, sheetValue = 0;

    if (drugs.length === 0) {
      sheet.addRow(["No drugs removed in this year."]);
    } else {
      drugs.forEach(d => {
        const qty = Number(d.quantity) || 0;
        const hasPrice = d.price != null;
        const m = (typeof d.month === "number" && d.month >= 0 && d.month <= 11)
          ? d.month : "";
        const r = sheet.addRow([
          d.drugName || "-",
          d.location || "-",
          d.expiryDate || "-",
          qty,
          d.batchNumber || "-",
          d.removedAt || "-",
          m === "" ? "" : (m + 1),           // Month# 1-12 for SUMIF
          hasPrice ? (d.price / 100) : null   // Unit price in £ (blank if none)
        ]);
        const rn = r.number;
        // Value (£) = unit price × qty, as a live formula with a cached result
        // so viewers show the figure before any manual recalc.
        const cachedValue = hasPrice ? (d.price / 100) * qty : 0;
        r.getCell(9).value = { formula: `${COL.unit}${rn}*${COL.qty}${rn}`,
                               result: cachedValue };
        r.getCell(8).numFmt = "0.00";
        r.getCell(9).numFmt = "0.00";
        sheetPackets += qty;
        sheetValue += cachedValue;
        if (m !== "") monthValue[m] += cachedValue;
        if (!hasPrice) {
          fill(r.getCell(8), AMBER_ARGB);   // unit price
          fill(r.getCell(9), AMBER_ARGB);   // value
          if (m !== "") monthMissing[m] = true;
        }
      });
    }

    const lastDataRow = drugs.length === 0 ? firstDataRow - 1
      : headerRowNum + drugs.length;

    if (drugs.length > 0) {
      totalPacketsRow.getCell(2).value = {
        formula: `SUM(${COL.qty}${firstDataRow}:${COL.qty}${lastDataRow})`,
        result: sheetPackets };
      totalValueRow.getCell(2).value = {
        formula: `SUM(${COL.value}${firstDataRow}:${COL.value}${lastDataRow})`,
        result: sheetValue };
      totalValueRow.getCell(2).numFmt = "0.00";
    } else {
      totalPacketsRow.getCell(2).value = 0;
      totalValueRow.getCell(2).value = 0;
    }

    boldRow(sheet.getRow(1));
    sheet.getRow(1).font = { bold: true, size: 14 };
    boldRow(sheet.getRow(2));
    boldRow(totalPacketsRow);
    boldRow(totalValueRow);

    sheet.getColumn(1).width = 34;   // Drug
    sheet.getColumn(2).width = 20;   // Location
    sheet.getColumn(3).width = 12;   // Expiry
    sheet.getColumn(4).width = 7;    // Qty
    sheet.getColumn(5).width = 16;   // Batch
    sheet.getColumn(6).width = 12;   // Removed on
    sheet.getColumn(7).width = 8;    // Month#
    sheet.getColumn(7).hidden = true;
    sheet.getColumn(8).width = 13;   // Unit price
    sheet.getColumn(9).width = 12;   // Value

    sheet.views = [{ state: "frozen", ySplit: headerRowNum }];

    sheetInfo.push({ ph, sheetName, monthMissing, monthValue,
                     hasData: drugs.length > 0 });
  }

  // ----------------------------------------------------------------------
  // SUMMARY TAB (value-only cells, live SUMIF formulas)
  // ----------------------------------------------------------------------
  summary.addRow([groupName || "Pharmace"]);
  summary.addRow([`Removed drugs — ${year}  (values £)`]);
  summary.addRow([`Exported: ${timestamp}`]);
  summary.addRow([]);

  const header = ["Pharmacy", ...MONTHS, "Total"];
  const headerRow = summary.addRow(header);
  boldRow(headerRow);
  headerRow.eachCell(cell => fill(cell, HEADER_ARGB));

  summary.getRow(1).font = { bold: true, size: 14 };
  boldRow(summary.getRow(2));

  const firstSummaryDataRow = headerRow.number + 1;

  for (const info of sheetInfo) {
    const { sheetName, monthMissing, monthValue, hasData } = info;
    const ref = sheetRef(sheetName);

    const row = summary.addRow([shownName(info.ph.pharmacyName)]);
    row.getCell(1).font = { bold: true };

    let rowTotal = 0;
    for (let m = 0; m < 12; m++) {
      const cell = row.getCell(m + 2);
      if (hasData) {
        cell.value = { formula:
          `SUMIF(${ref}!${COL.month}:${COL.month},${m + 1},${ref}!${COL.value}:${COL.value})`,
          result: monthValue[m] };
      } else {
        cell.value = 0;
      }
      cell.numFmt = "0.00";
      rowTotal += monthValue[m] || 0;
      if (monthMissing[m]) fill(cell, AMBER_ARGB);
    }

    const totalCell = row.getCell(14);
    totalCell.value = { formula: `SUM(B${row.number}:M${row.number})`,
                        result: rowTotal };
    totalCell.numFmt = "0.00";
    if (monthMissing.some(Boolean)) fill(totalCell, AMBER_ARGB);
  }

  const lastSummaryDataRow = firstSummaryDataRow + sheetInfo.length - 1;

  const totalRow = summary.addRow(["TOTAL"]);
  boldRow(totalRow);
  const colHasMissing = new Array(12).fill(false);
  for (const info of sheetInfo) {
    for (let m = 0; m < 12; m++) if (info.monthMissing[m]) colHasMissing[m] = true;
  }
  if (sheetInfo.length > 0) {
    const colTotals = new Array(12).fill(0);
    for (const info of sheetInfo) {
      for (let m = 0; m < 12; m++) colTotals[m] += info.monthValue[m] || 0;
    }
    let grandTotal = 0;
    for (let m = 0; m < 12; m++) {
      const colLetter = String.fromCharCode("B".charCodeAt(0) + m); // B..M
      const cell = totalRow.getCell(m + 2);
      cell.value = { formula:
        `SUM(${colLetter}${firstSummaryDataRow}:${colLetter}${lastSummaryDataRow})`,
        result: colTotals[m] };
      cell.numFmt = "0.00";
      grandTotal += colTotals[m];
      if (colHasMissing[m]) fill(cell, AMBER_ARGB);
    }
    const grand = totalRow.getCell(14);
    grand.value = { formula:
      `SUM(N${firstSummaryDataRow}:N${lastSummaryDataRow})`, result: grandTotal };
    grand.numFmt = "0.00";
    if (colHasMissing.some(Boolean)) fill(grand, AMBER_ARGB);
  }

  summary.addRow([]);
  const legend = summary.addRow([
    "Each cell shows the total value (£) of drugs removed that month, summed " +
    "live from the matching pharmacy tab — edit a value there and this updates. " +
    "Amber = one or more drugs that month had no price, so the figure is understated."
  ]);
  legend.getCell(1).font = { italic: true, color: { argb: "FF6B7280" } };
  summary.mergeCells(legend.number, 1, legend.number, 14);

  // Narrow month columns (2b); keep Pharmacy + Total readable.
  summary.getColumn(1).width = 26;          // Pharmacy
  for (let m = 0; m < 12; m++) summary.getColumn(m + 2).width = 8;  // Jan..Dec
  summary.getColumn(14).width = 10;         // Total

  // Recalculate formulas on open (in addition to the cached results above).
  workbook.calcProperties.fullCalcOnLoad = true;

  // ----------------------------------------------------------------------
  // DOWNLOAD
  // ----------------------------------------------------------------------
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  });
  const safeGroup = sanitizeSheetName(groupName || "Pharmace").replace(/\s+/g, "_");
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `RemovedDrugs_${safeGroup}_${year}_${dateStamp}.xlsx`;
  a.click();
  window.URL.revokeObjectURL(url);
}
